import { Injectable } from '@angular/core';
import { iif, of } from 'rxjs';
import { catchError, exhaustMap, map, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PermissionTypes } from '@arc/arc-shared-frontend';
import {
  LoadingSpinnerService,
  mapPaymentRules,
  PaymentAcceptanceRules,
  PaymentAcceptanceRulesServiceFactory,
  PaymentRemittanceRulesApiResponse,
  PaymentRulesResponseV1,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  GetPaymentRuleCompleteAction,
  GetPaymentRuleErrorAction,
  UpdateRUMPaymentRulesTagsAction,
} from '../actions/payment-rules.actions';

import { PaymentRulesActions } from '../actions';
import { BaseEffect } from '../../helpers/base-effect';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { selectGetPaymentRulesPayload } from '../selectors';
import {
  HostedPaymentsPadResultComponentPaymentRulesActions,
  PoaPadResultComponentPaymentRulesActions,
  PoiPadResultComponentPaymentRulesActions,
} from '../../component-actions';

@Injectable()
export class PaymentRulesEffects extends BaseEffect {
  constructor(
    private paymentAcceptanceRulesServiceFactory: PaymentAcceptanceRulesServiceFactory,
    private permissions: PermissionTypes,
    private store: Store,
    actions$: Actions,
    loadingSpinnerService: LoadingSpinnerService,
  ) {
    super(actions$, loadingSpinnerService);
  }

  updateRUMPaymentRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PaymentRulesActions.GetPaymentRuleCompleteAction,
        PoaPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
        PoiPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
        HostedPaymentsPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
      ),
      map((action) => action.payload),
      exhaustMap((payload: PaymentRulesResponseV1) => {
        return of(
          UpdateRUMPaymentRulesTagsAction({
            payload,
          }),
        );
      }),
    ),
  );

  getPaymentAcceptanceRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentRulesActions.GetPaymentAcceptanceRulesAction),
      withLatestFrom(
        this.permissions.isPermitted$(
          'arc-permission-section-payments-permission-stripe:create',
        ),
      ),
      concatLatestFrom(() => [this.store.select(selectGetPaymentRulesPayload)]),
      exhaustMap(([[, isStripePermitted], getPaymentRulesPayload]) => {
        return this.paymentAcceptanceRulesServiceFactory
          .create()
          .getPaymentAcceptanceRulesV1(
            getPaymentRulesPayload.tenantId,
            getPaymentRulesPayload.originApplication,
            getPaymentRulesPayload.cardMarsToken,
          )
          .pipe(
            map((response: PaymentAcceptanceRules) => {
              return GetPaymentRuleCompleteAction({
                payload: mapPaymentRules(
                  response,
                  isStripePermitted,
                  getPaymentRulesPayload.originApplication,
                ),
              });
            }),
            catchError(() => of(GetPaymentRuleErrorAction())),
          );
      }),
    ),
  );

  /** istanbul ignore next */
  loadUntilPaymentRulesComplete$ = createEffect(
    () =>
      this.loadUntilActionsComplete(
        [PaymentRulesActions.GetPaymentAcceptanceRulesAction.type],
        [
          PaymentRulesActions.GetPaymentRuleCompleteAction.type,
          PaymentRulesActions.GetPaymentRuleErrorAction.type,
        ],
      ),
    { dispatch: false },
  );

  getPaymentRemittanceRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentRulesActions.GetPaymentRemittanceRulesAction),
      map((action) => action.payload),
      withLatestFrom(
        this.permissions.isPermitted$(
          'arc-permission-section-payments-permission-payment-remittance:read',
        ),
        this.permissions.isPermitted$(
          'arc-permission-section-payments-permission-payment-remittance:create',
        ),
      ),
      exhaustMap(
        ([
          payload,
          hasRemittanceReadPermission,
          hasRemittanceCreatePermission,
        ]) => {
          return iif(
            () => hasRemittanceReadPermission && hasRemittanceCreatePermission,
            this.paymentAcceptanceRulesServiceFactory
              .create()
              .getPaymentRemittanceRulesV1(payload)
              .pipe(
                map((response: PaymentRemittanceRulesApiResponse) => {
                  return PaymentRulesActions.GetPaymentRemittanceRulesCompleteAction(
                    {
                      payload: response.paymentSettings,
                    },
                  );
                }),
                catchError(() =>
                  of(PaymentRulesActions.GetPaymentRemittanceRuleErrorAction()),
                ),
              ),
            of(
              PaymentRulesActions.GetPaymentRemittanceRulesCompleteAction({
                payload: null,
              }),
            ),
          );
        },
      ),
    ),
  );
}
